'use client';

import { useState, useMemo } from 'react';
import { PortableText } from '@portabletext/react';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GroqImage } from '@/helpers/Image';
import { cn } from '@/lib/utils';
import type { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';

import dots from '@/public/fabio-affiliate/dots-pricing-bg.png';
import Image from 'next/image';
import { Check } from 'lucide-react';

type AmountFilter = '100k' | '50k';

const FILTERS: { label: string; value: AmountFilter; matches: string[] }[] = [
  { label: '$50,000', value: '50k', matches: ['50,000', '50000', '50k'] },
  { label: '$100,000', value: '100k', matches: ['100,000', '100000', '100k'] },
];

export default function DealsSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const [activeFilter, setActiveFilter] = useState<AmountFilter>('100k');

  const filteredDeals = useMemo(() => {
    if (!data?.dealsItems?.length) return [];
    const filter = FILTERS.find((f) => f.value === activeFilter);
    if (!filter) return data.dealsItems;
    const filtered = data.dealsItems.filter((deal) => {
      const haystack =
        `${deal?.amount ?? ''} ${deal?.title ?? ''} ${deal?.subtitle ?? ''} ${deal?.tag ?? ''}`.toLowerCase();
      return filter.matches.some((m) => haystack.includes(m.toLowerCase()));
    });
    // Fallback: if nothing matched, show all so the section never appears empty
    return filtered.length > 0 ? filtered : data.dealsItems;
  }, [data?.dealsItems, activeFilter]);

  if (!data?.dealsChip && !data?.dealsHeading && !data?.dealsItems?.length)
    return null;

  return (
    <section className="pt-[10rem] pb-[10rem] lg:pb-[20rem]">
      <div className="mx-auto w-[min(140rem,92%)]">
        <div className="flex flex-col items-center text-center mb-[5rem] gap-[1rem]">
          {data?.dealsChip && (
            <span className="italic text-black font-blacker text-[2rem] font-[400]">
              {data.dealsChip}
            </span>
          )}
          {data?.dealsHeading && (
            <div className="richtext richtext-center prose-h2:text-black prose-h2:font-geist  prose-h2:font-[500] prose-h2:leading-[115%] prose-h2:tracking-[-0.25rem] lg:prose-h2:tracking-[-0.4rem] prose-em:font-blacker prose-em:font-[400]">
              <PortableText
                value={data.dealsHeading}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {data?.dealsItems?.length > 0 && (
          <div className="flex justify-center mb-[3rem]">
            <div className="inline-flex items-center rounded-full bg-black/5 gap-[0.5rem] p-[.5rem]">
              {FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setActiveFilter(filter.value)}
                  className={cn(
                    'px-[3rem] py-[1.25rem] rounded-full  transition-colors font-geist text-[2.25rem] font-[500]',
                    activeFilter === filter.value
                      ? 'bg-black text-white'
                      : 'text-black/50 hover:bg-[#F5F5F5]',
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {filteredDeals.length > 0 && (
          <div className="grid grid-cols-1 items-stretch w-full lg:grid-cols-2 gap-[3rem]">
            {filteredDeals.map((deal, i) => (
              <article
                key={`deal-${deal?.title ?? i}`}
                className="flex flex-col h-full gap-[1rem]"
              >
                <div
                  className={`bg-black p-[4rem] overflow-clip h-[38rem] flex flex-col justify-between relative rounded-[0.75rem] shrink-0 ${deal?.title === 'Challenge Deal' ? 'bg-gradient-to-br from-[#205043] to-[#1D4137]' : 'bg-black'}`}
                >
                  <div className="flex flex-col gap-[1.5rem]">
                    <Image
                      src={dots}
                      alt=""
                      className="absolute inset-0 w-full h-full pointer-events-none z-[1]"
                    />
                    {deal?.tag && (
                      <span
                        className={` leading-none w-fit relative z-[5] font-geist text-[2rem] rounded-full py-[0.625rem] px-[1.5rem] font-[700] ${deal?.title === 'Challenge Deal' ? 'text-white bg-[#1A1A1A]' : 'black bg-[#67A796]'}`}
                      >
                        {deal.tag}
                      </span>
                    )}

                    {(deal?.title || deal?.subtitle) && (
                      <div className="flex relative flex-col gap-[0.5rem] z-[5]">
                        {deal?.title && (
                          <p className="font-geist text-[4rem] font-[400] leading-[112%] tracking-[-.96px] text-white m-0">
                            {deal.title}
                          </p>
                        )}
                        {deal?.subtitle && (
                          <p className="m-0 text-white font-geist text-[2.25rem] leading-[112%] tracking-[-.18px]">
                            {deal.subtitle}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {(deal?.priceCurrent ||
                    deal?.priceOriginal ||
                    deal?.discountText) && (
                    <div className="flex relative items-baseline gap-[1rem] z-[5]">
                      {deal?.priceCurrent && (
                        <span className="leading-none text-white text-[5rem] font-[500] font-geist">
                          {deal.priceCurrent}
                        </span>
                      )}
                      {deal?.priceOriginal && (
                        <span className="leading-none text-white line-through font-geist text-[2.5rem] font-[400]">
                          {deal.priceOriginal}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {(deal?.rulesTitle || deal?.rules?.length) && (
                  <div className="flex flex-col flex-1 justify-between border p-[4rem] rounded-[0.75rem] border-black/10 shrink-0">
                    <div>
                      {deal?.rulesTitle && (
                        <p className="uppercase text-[#1A1A1A]/50 font-geist text-[1.75rem] font-[600]">
                          {deal.rulesTitle}
                        </p>
                      )}
                      {deal?.rules?.length > 0 && (
                        <ul className="flex flex-col gap-[1.5rem]">
                          {deal.rules.map(
                            (rule, j) =>
                              rule?.text && (
                                <li
                                  key={`deal-rule-${j}`}
                                  className="flex items-center gap-[1.5rem]"
                                >
                                  <div className="w-[3rem] h-[3rem] rounded-full bg-[#003828]/[15%] flex items-center justify-center ">
                                    <Check size={16} color="#003828" />
                                  </div>
                                  <span className="m-0 leading-none text-[#1A1A1A] font-geist text-[2.25rem] font-[400]">
                                    {rule.text}
                                  </span>
                                </li>
                              ),
                          )}
                        </ul>
                      )}
                    </div>

                    {deal?.cta && (
                      <div className="w-full mt-[6rem]">
                        <CTAButton data={deal.cta} className="!w-full" />
                      </div>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
